/**
 * The signature moment — the most consequential thing this product does.
 *
 * The loop, and every part of it is load-bearing:
 *
 *   1. `GET /agent/pending/{id}` with the approver mandate AND both reason fields → `to_sign`,
 *      the exact canonical bytes per outcome. Supply fewer than all three and `to_sign` is
 *      simply absent.
 *   2. The browser signs those bytes with the approver's own Ed25519 key.
 *   3. `POST /agent/resolve` → the SERVER verifies the signature against the `did:key` in that
 *      approver's own mandate.
 *
 * **The footgun this component exists to prevent:** the reason code is inside the signed body.
 * Fetch `to_sign` with one code and resolve with another and the signature covers a different
 * body — a 403 that says nothing useful. So the code is chosen BEFORE the bytes are fetched,
 * and the same value is carried through both calls; there is no path where they can differ.
 *
 * And the key is device-bound, which the UI has to say out loud. The private half never leaves
 * this browser — that is what makes the signature evidence rather than a server assertion — so
 * an approver on a new machine is not broken, they are un-enrolled, and those look identical
 * unless somebody says so.
 */

import { useCallback, useEffect, useState } from "react";
import { State, fmtMoney, useAsync } from "@authoxi/ui-shared";
import type { Api, Mandate, Pending, ReasonCode, ReasonCodes } from "../api";
import { type ApproverKey, createApproverKey, loadApproverKey, signResolution } from "../lib/signer";

export function Approvals({ api, onResolved }: { api: Api; onResolved: () => void }) {
  const queue = useAsync(() => api.pending());
  const approverMandates = useAsync(() => api.mandates({ subject_kind: "human" }));
  const codes = useAsync(() => api.reasonCodes());
  const [key, setKey] = useState<ApproverKey | null>(() => loadApproverKey());

  const waiting = (queue.data?.pending ?? []).filter((p) => p.status === "pending");
  const settled = (queue.data?.pending ?? []).filter((p) => p.status !== "pending");

  // An approver mandate whose did matches THIS device's key. Without one the queue is
  // unsignable, and the reason is not obvious from looking at it.
  const mine = (approverMandates.data ?? []).find(
    (m) => key && m.subject_did === key.did && m.status === "active",
  );

  return (
    <>
      <div className="view-head">
        <span className="label">Approvals</span>
        <h1>{waiting.length ? "Sign, or refuse." : "Nothing waiting."}</h1>
        <p>
          Your signature is Ed25519 over the exact action, and it names you — signing uses your
          key and we never hold it. It cannot be repudiated later, which is the point, so the
          screen shows you everything you are signing before you do.
        </p>
      </div>

      <Enrolment
        api={api}
        keyPair={key}
        mandate={mine}
        mandates={approverMandates.data ?? []}
        onEnrolled={(k) => {
          setKey(k);
          void approverMandates.reload();
        }}
      />

      {queue.loading && <State kind="loading" title="Reading the queue…" />}
      {queue.error && <State kind="error" title="Could not read the queue" detail={queue.error} />}

      {queue.data && waiting.length === 0 && (
        <State
          kind="empty"
          title="Queue empty"
          detail="Agents are deciding within their mandates. Nothing is holding for a human."
        />
      )}

      {waiting.map((p) => (
        <ApprovalCard
          key={p.pending_id}
          api={api}
          pending={p}
          approverKey={key}
          approverMandate={mine}
          codes={codes.data}
          onDone={() => {
            void queue.reload();
            onResolved();
          }}
        />
      ))}

      {settled.length > 0 && (
        <>
          <div className="rule" style={{ marginTop: "var(--s7)" }}>
            <span className="label">Settled</span>
          </div>
          <div className="timeline">
            {settled.slice(0, 10).map((p) => (
              <div className={`tl ${p.resolution === "approve" ? "allow" : "deny"}`} key={p.pending_id}>
                <div className="tl-time">{p.created_at?.slice(11, 19) ?? ""}</div>
                <div className="tl-what">
                  {p.action} {p.amount !== "0" && fmtMoney(p.amount, p.currency)}
                </div>
                <div className="tl-why">
                  {p.resolution === "approve" ? "approved" : "refused"} · signed · ed25519 ✓
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

/**
 * Enrol THIS device as an approver.
 *
 * The private key is minted here and stays here; only the `did:key` is published, inside a
 * human mandate. That is what makes the signature evidence — a server that could produce it
 * would make it worthless — and it is also why approvals only work from the browser that
 * enrolled. Said plainly, because "the buttons are disabled and I do not know why" is the
 * alternative.
 */
function Enrolment({
  api,
  keyPair,
  mandate,
  mandates,
  onEnrolled,
}: {
  api: Api;
  keyPair: ApproverKey | null;
  mandate: Mandate | undefined;
  mandates: Mandate[];
  onEnrolled: (k: ApproverKey) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (mandate) {
    return (
      <div className="soft pad" data-testid="approver-ready">
        <span className="v dim v-allow">this device can sign</span>{" "}
        <span className="sm muted">
          up to {fmtMoney(mandate.remaining, mandate.currency)} · the private key is in this
          browser and has never left it.
        </span>
      </div>
    );
  }

  const othersExist = mandates.some((m) => m.status === "active");

  return (
    <div className="needs-you" data-testid="approver-missing">
      <p className="label" style={{ color: "var(--escalate-text)" }}>
        This device cannot sign yet
      </p>
      <p className="sm" style={{ margin: "var(--s2) 0 0", color: "var(--ink-2)", maxWidth: "var(--measure)" }}>
        {keyPair
          ? "This browser holds a key, but no active approver mandate names it. Enrol it below, or sign from the device that already has one."
          : "Signing needs a key that lives on this device and never leaves it — that is what makes your approval evidence rather than something our server asserted about you."}
        {othersExist && " Another device is already enrolled; a queue can have several approvers."}
      </p>
      <button
        className="btn btn-go"
        style={{ marginTop: "var(--s4)" }}
        disabled={busy}
        data-testid="enrol-approver"
        onClick={async () => {
          setBusy(true);
          setErr(null);
          try {
            const k = keyPair ?? createApproverKey();
            await api.issueMandate({
              subject_id: `approver-${k.did.slice(-8)}`,
              subject_kind: "human",
              subject_did: k.did,
              budget: "50000.00",
              allowed_actions: ["payment", "refund", "infra_change"],
            });
            onEnrolled(k);
          } catch (e) {
            setErr(e instanceof Error ? e.message : String(e));
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? "…" : "Enrol this device as an approver"}
      </button>
      {err && <p className="sm" style={{ color: "var(--deny-text)" }}>{err}</p>}
    </div>
  );
}

function ApprovalCard({
  api,
  pending,
  approverKey,
  approverMandate,
  codes,
  onDone,
}: {
  api: Api;
  pending: Pending;
  approverKey: ApproverKey | null;
  approverMandate: Mandate | undefined;
  codes: ReasonCodes | null;
  onDone: () => void;
}) {
  const [sheet, setSheet] = useState<"approve" | "deny" | null>(null);
  const money = pending.amount && pending.amount !== "0";

  return (
    <article className="approval" data-testid="approval">
      <div className="approval-head">
        <div>
          <span className="v v-escalate">escalate</span>
          <div className="approval-amount" style={{ margin: "var(--s3) 0 var(--s2)" }}>
            {money ? fmtMoney(pending.amount, pending.currency) : pending.action}
          </div>
          <div className="sm" style={{ color: "var(--ink-2)" }}>
            <span className="mono">{pending.agent_id}</span>
          </div>
        </div>
      </div>
      <div className="approval-facts">
        <div className="fact">
          <div className="k">Why you</div>
          <div className="v-txt">
            {money
              ? "over this mandate's escalation threshold"
              : /* The non-money path renders differently on purpose — "no budget impact" is the
                   useful sentence, and a card that only knows how to talk about amounts says
                   nothing here. */
                "this action always needs a human, at any price — no budget impact"}
          </div>
        </div>
        <div className="fact">
          <div className="k">Mandate</div>
          <div className="v-txt mono">{pending.mandate_id}</div>
        </div>
        <div className="fact">
          <div className="k">Action</div>
          <div className="v-txt mono">{pending.action}</div>
        </div>
      </div>
      <div className="approval-act">
        <button
          className="btn btn-go"
          disabled={!approverKey || !approverMandate}
          data-testid="approve"
          onClick={() => setSheet("approve")}
        >
          Approve &amp; sign
        </button>
        <button
          className="btn"
          disabled={!approverKey || !approverMandate}
          data-testid="refuse"
          onClick={() => setSheet("deny")}
        >
          Refuse
        </button>
      </div>

      {sheet && approverKey && approverMandate && (
        <SignSheet
          api={api}
          pending={pending}
          resolution={sheet}
          approverKey={approverKey}
          approverMandate={approverMandate}
          codes={codes}
          onClose={() => setSheet(null)}
          onDone={() => {
            setSheet(null);
            onDone();
          }}
        />
      )}
    </article>
  );
}

/**
 * The signing sheet.
 *
 * It exists because the product's most consequential action should require a deliberate second
 * beat — and because showing exactly what is about to be signed is the difference between an
 * approval and a click.
 *
 * **A deny requires a reason code; an approve defaults to `within_policy`.** The picked code is
 * inside the signed bytes, so it is chosen before `to_sign` is fetched and the same value is
 * carried into `resolve()`. The taxonomy is the loss-intelligence asset, which is why it is
 * part of the signature rather than a form field beside it.
 */
function SignSheet({
  api,
  pending,
  resolution,
  approverKey,
  approverMandate,
  codes,
  onClose,
  onDone,
}: {
  api: Api;
  pending: Pending;
  resolution: "approve" | "deny";
  approverKey: ApproverKey;
  approverMandate: Mandate;
  codes: ReasonCodes | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const options: ReasonCode[] = (resolution === "approve" ? codes?.approve : codes?.deny) ?? [];
  const [code, setCode] = useState<string>("");
  const [toSign, setToSign] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!code && options.length) setCode(options[0].reason_code);
  }, [code, options]);

  // Both halves come from the response. Neither is invented here.
  const reasonClass = options.find((o) => o.reason_code === code)?.reason_class ?? "";

  // Fetch the bytes for the CHOSEN code. Re-fetched whenever it changes, so the preview below
  // is always the body that will actually be signed.
  const loadBytes = useCallback(async () => {
    if (!code || !reasonClass) return;
    setErr(null);
    try {
      const detail = await api.pendingDetail(pending.pending_id, {
        approver_mandate_id: approverMandate.mandate_id,
        reason_class: reasonClass,
        reason_code: code,
      });
      setToSign(detail.to_sign?.[resolution] ?? null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [api, approverMandate.mandate_id, code, pending.pending_id, reasonClass, resolution]);

  useEffect(() => {
    void loadBytes();
  }, [loadBytes]);

  return (
    <div className="sheet" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sheet-in" data-testid="sign-sheet">
        <h2 className="md" style={{ margin: "var(--s4) 0 var(--s3)" }}>
          {resolution === "approve" ? "Sign this approval" : "Sign this refusal"}
        </h2>
        <p className="sm" style={{ margin: "0 0 var(--s5)", color: "var(--ink-2)" }}>
          You are about to sign, with your own key, that you {resolution === "approve" ? "approved" : "refused"} this
          exact action. It names you and you cannot deny it later.
        </p>

        <dl className="kv" style={{ gridTemplateColumns: "104px 1fr" }}>
          <dt>agent</dt>
          <dd className="mono">{pending.agent_id}</dd>
          <dt>action</dt>
          <dd>{pending.action}</dd>
          {pending.amount !== "0" && (
            <>
              <dt>amount</dt>
              <dd>{fmtMoney(pending.amount, pending.currency)}</dd>
            </>
          )}
          <dt>signed by</dt>
          <dd className="mono">{approverKey.did.slice(0, 28)}…</dd>
        </dl>

        <p className="label" style={{ marginTop: "var(--s5)" }}>
          Reason {resolution === "deny" && <span style={{ color: "var(--deny-text)" }}>· required</span>}
        </p>
        <select
          className="input"
          value={code}
          data-testid="reason-code"
          onChange={(e) => {
            setToSign(null);
            setCode(e.target.value);
          }}
        >
          {options.map((c) => (
            <option value={c.reason_code} key={c.reason_code}>
              {c.reason_code}
            </option>
          ))}
        </select>
        <p className="xs muted" style={{ marginTop: "var(--s2)" }}>
          This code is <strong>inside</strong> the signed body — change it and the bytes below
          change with it.
        </p>

        <p className="label" style={{ marginTop: "var(--s4)" }}>
          Exactly what will be signed
        </p>
        <pre className="code" data-testid="to-sign" style={{ wordBreak: "break-all" }}>
          {toSign ?? "…"}
        </pre>

        {err && (
          <p className="sm" style={{ color: "var(--deny-text)" }} data-testid="sign-error">
            {err}
          </p>
        )}

        <div className="row" style={{ marginTop: "var(--s6)" }}>
          <button
            className="btn btn-go grow"
            disabled={!toSign || busy}
            data-testid="do-sign"
            onClick={async () => {
              setBusy(true);
              setErr(null);
              try {
                await api.resolve({
                  pending_id: pending.pending_id,
                  resolution,
                  reason_class: reasonClass,
                  // The SAME code the bytes were fetched with. There is no path where these
                  // two can differ, which is the entire reason this is one component.
                  reason_code: code,
                  approver_mandate_id: approverMandate.mandate_id,
                  signature: signResolution(approverKey, toSign!),
                });
                onDone();
              } catch (e) {
                setErr(e instanceof Error ? e.message : String(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? "Signing…" : "Sign with my key"}
          </button>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
